import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  ReactElement, Suspense, useCallback, useEffect, useMemo, useState
} from 'react';
import {
  FiPlus
} from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ModalRunCreate from '@/app/evaluations/_modalRunCreate/modalRunCreate';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { StatusIcon } from '@/components/statusIcon/statusIcon';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { useCreateRun, useEvalRuns, useSetDeprecation } from '@/hooks/queries/useEvaluations';
import { useOrgUsersMapQuery } from '@/hooks/queries/useOrg';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { useRunProgress } from '@/hooks/useRunProgress';
import {
  RunCreateReq, RunMetricDataMap, RunProgressMessage, TERMINAL_RUN_STATUSES
} from '@/types/evals';
import { isFiniteNumber } from '@/utils/numberUtils';
import { parseParams } from '@/utils/searchParams';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<Record<string, unknown>>();

type SearchParams = {
  eid: string;
};

// Surface deprecation rejections from the backend (eval / metric / source) with
// a clearer message that points the user to the un-deprecate action. Any other
// error message is passed through unchanged.
export function formatRunCreateError( raw: string ): string {
  if( /deprecated/i.test( raw ) ) {
    return `Cannot create run — ${ raw }. Un-deprecate it first to run.`;
  }
  return raw;
}

/**
 * Streams progress for a single active run.
 * Renders nothing - only manages the SSE connection and relays updates via callbacks.
 */
function RunStreamWatcher( { runId, onProgress, onComplete }: {
  runId: string;
  onProgress: ( runId: string, progress: RunProgressMessage ) => void;
  onComplete: ( runId: string ) => void;
} ) {
  const { progress, isTerminal } = useRunProgress( runId, true );

  useEffect( () => {
    if( progress ) onProgress( runId, progress );
  }, [ progress, runId, onProgress ] );

  useEffect( () => {
    if( isTerminal ) onComplete( runId );
  }, [ isTerminal, runId, onComplete ] );

  return null;
}

function EvaluationRunsPage() {
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const {
    eid: qEvaluationId = ''
  } = parseParams<SearchParams>( searchParams );
  const [ showModalRun, setShowModalRun ] = useState<boolean>( false );
  const [ useLocalZone ] = useLocalZonePreference();
  const [ progressMap, setProgressMap ] = useState<Record<string, RunProgressMessage>>( {} );

  // React Query hooks
  const { data: evalRunsData, isLoading, refetch: refetchEvalRuns } = useEvalRuns( qEvaluationId );
  const { data: usersMap } = useOrgUsersMapQuery();
  const setDeprecationMutation = useSetDeprecation( qEvaluationId );
  const createRunMutation = useCreateRun( qEvaluationId );

  const evaluation = evalRunsData?.evaluation;
  const runs = evalRunsData?.runs || [];

  // Find runs that need streaming (non-terminal status)
  const activeRunIds = useMemo( () => runs
    .filter( r => !( TERMINAL_RUN_STATUSES as readonly string[] ).includes( r.run.status ) )
    .map( r => r.run.id ),
  [ runs ] );

  const handleStreamProgress = useCallback( ( runId: string, progress: RunProgressMessage ) => {
    setProgressMap( prev => ( { ...prev, [ runId ]: progress } ) );
  }, [] );

  const handleStreamComplete = useCallback( ( runId: string ) => {
    setProgressMap( prev => {
      const next = { ...prev };
      delete next[ runId ];
      return next;
    } );
    refetchEvalRuns();
  }, [ refetchEvalRuns ] );

  // Derive metric data from runs
  const [ runMetricDataMap, metricKeys ] = useMemo( () => {
    const data: RunMetricDataMap = {};
    const metricKeySet: Set<string> = new Set();

    if ( runs.length === 0 ) return [ data, [] as string[] ];

    for ( const run of runs ) {
      if ( !run.details ) continue;

      for ( const metric of run.details.metrics ) {
        const combinedIndex = `${ metric.metricProviderId }|${ metric.metricName }`;
        metricKeySet.add( combinedIndex );

        data[ run.run.id ] = {
          ...data[ run.run.id ],
          [ combinedIndex ]: metric
        };
      }
    }

    const sortedUniqueMetricKeys = Array.from( metricKeySet ).sort();
    return [ data, sortedUniqueMetricKeys ];
  }, [ runs ] );

  /* HELPERS */
  const getUserDisplayName = ( userId?: string ): string => {
    if ( !userId ) return '-';

    const user = usersMap?.get( userId );
    return user?.name || user?.email || userId;
  };

  // Memoize table columns
  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>( () => {
    return [
      columnHelper.accessor( 'runId', {
        id: 'runId',
        header: 'Run ID',

        size: 250,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 250, styles: ColumnModEnums.NO_OVERFLOW } ),
          fixedWidth: '250px'
        }
      } ),
      columnHelper.accessor( 'ranBy', {
        id: 'ranBy',
        header: 'Ran By',
        size: 200,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 200, styles: ColumnModEnums.NO_OVERFLOW } ),
          fixedWidth: '200px'
        }
      } ),
      columnHelper.accessor( 'status', {
        id: 'status',
        header: 'Status',
        size: 120,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 120, styles: ColumnModEnums.CENTER } ),
          fixedWidth: '120px'
        }
      } ),
      columnHelper.accessor( 'ranAt', {
        id: 'ranAt',
        header: 'Ran At',
        size: 250,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 250, styles: ColumnModEnums.NO_OVERFLOW } ),
          fixedWidth: '250px'
        }
      } ),
      columnHelper.accessor( 'conversations', {
        id: 'conversations',
        header: '# Conversations',
        size: 160,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 160, styles: ColumnModEnums.CENTER } ),
          fixedWidth: '160px'
        }
      } ),
      ...metricKeys.map( metricKey => {
        const [ _, metricName ] = metricKey.split( '|' );

        return columnHelper.accessor( metricKey, {
          id: metricKey,
          header: metricName,
          size: 190,
          cell: info => info.getValue(),
          meta: {
            classStr: buildColumnClassString( { width: 190, styles: [ ColumnModEnums.CENTER, ColumnModEnums.NO_OVERFLOW ] } ),
            fixedWidth: '190px'
          }
        } );
      } )
    ];
  }, [ metricKeys ] );

  // Memoize table data
  const tableData = useMemo<Record<string, unknown>[]>( () => {
    const data: Record<string, unknown>[] = [];

    for( const [ i, { run } ] of runs.entries() ) {
      const progressData = progressMap[ run.id ];

      const row: Record<string, unknown> = {
        _rid: run.id,
        status: progressData ? (
          <div className={ styles.status_with_progress }>
            <StatusIcon key={ i + '-status' } status={ run.status } />
            <span className={ styles.progress_text }>{ progressData.syncPercentComplete }%</span>
          </div>
        ) : (
          <StatusIcon key={ i + '-status' } status={ run.status } />
        ),
        runId: run.id,
        ranBy: getUserDisplayName( run.createdBy ),
        ranAt: (
          <TimeFormatter
            key={ run.id }
            sourceType={ TimeSourceTypeEnum.ISO }
            value={ run.createdAt }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
            opts={ { useLocalZone } }
          />
        ),
        conversations: run.totalExamples || '-'
      };

      const runMetrics = runMetricDataMap[ run.id ];

      for( const metricKey of metricKeys ) {
        const avg = runMetrics?.[ metricKey ]?.averageScore;
        row[ metricKey ] = isFiniteNumber( avg ) ? avg.toFixed( 3 ) : '-';
      }

      data.push( row );
    }

    return data;
  }, [ runs, runMetricDataMap, metricKeys, evaluation?.id, useLocalZone, progressMap ] );

  // Create table instance with column pinning
  const runsTable = useNativeTable( {
    data: tableData,
    columns: tableColumns,
    initialState: {
      columnPinning: {
        left: [ 'runId', 'ranBy' ]
      }
    }
  } );

  const handleRowClick = useCallback( ( row: Record<string, unknown> ) => {
    const rid = row._rid;
    if ( !evaluation?.id || typeof rid !== 'string' ) return;
    const params = new URLSearchParams( { eid: evaluation.id, rid } );
    navigate( `/evaluations/results?${ params.toString() }` );
  }, [ evaluation?.id, navigate ] );

  const _setDeprecation = ( isDeprecated: boolean ) => {
    if( !evaluation ) return;

    setDeprecationMutation.mutate( { isDeprecated }, {
      onSuccess: () => {
        toast.success( `Evaluation ${ isDeprecated ? 'deprecated' : 'undeprecated' }` );
        refetchEvalRuns();
      },
      onError: ( error ) => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to update deprecation status';
        toast.error( errorMessage );
      }
    } );
  };


  /* HANDLERS */
  const onRunCreate = async( ret: RunCreateReq ) => {
    if( !evaluation ) {
      console.error( 'no evaluation context, unable to create run' );
      toast( 'no evaluation context, unable to create run' );
      return;
    }

    createRunMutation.mutate( ret, {
      onSuccess: () => {
        toast.success( 'Run created successfully' );
      },
      onError: ( error ) => {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create run';
        console.error( errorMessage );
        toast.error( formatRunCreateError( errorMessage ) );
      }
    } );
  };




  /* RENDERS */
  const renderInfo = (): ReactElement => (
    <>
      <span className={ clsx( styles.info_name ) }>
        <p>{ evaluation?.name }</p>
      </span>
      <span className={ clsx( styles.info_page ) }>
        <p>Runs</p>
      </span>
      <span className={ styles.info_actions }>
        <div className={ clsx( 'layout__row', styles.deprecate ) }>
          <span>Deprecated</span>
          <input
            checked={ evaluation?.isDeprecated }
            disabled={ setDeprecationMutation.isPending }
            onChange={ e => _setDeprecation( e.target.checked ) }
            type='checkbox'
          />
        </div>
        <Button
          variant='primary'
          icon={ <Icon icon={ FiPlus } /> }
          onClick={ () => setShowModalRun( !showModalRun ) }
          disabled={ evaluation?.isDeprecated }
          title={ evaluation?.isDeprecated ? 'Un-deprecate to create a run' : undefined }
        >Create Run
        </Button>
      </span>
    </>
  );

  const renderDescription = (): ReactElement => (
    <span className={ styles.info_description }>{ evaluation?.description }</span>
  );

  const renderStreamingIndicator = (): ReactElement | null => {
    if( activeRunIds.length === 0 ) return null;

    return (
      <div className={ clsx( 'layout__row', 'pad', styles.layout__actions ) }>
        <div className={ styles.streaming_indicator }>
          <span className={ styles.streaming_dot } />
          <span>Live — streaming { activeRunIds.length } active run{ activeRunIds.length > 1 ? 's' : '' }</span>
        </div>
      </div>
    );
  };

  const render = (): ReactElement => (
    <>
      { activeRunIds.map( id => (
        <RunStreamWatcher
          key={ id }
          runId={ id }
          onProgress={ handleStreamProgress }
          onComplete={ handleStreamComplete }
        />
      ) ) }
      {
        showModalRun && evaluation && (
          <ModalRunCreate
            cb={ onRunCreate }
            onClose={ () => setShowModalRun( !showModalRun ) }
          />
        )
      }

      <div className={ clsx( 'layout__row', styles.layout__info ) }>
        { renderInfo() }
      </div>
      <div className={ clsx( 'layout__row', 'pad', styles.layout__description ) }>
        { renderDescription() }
      </div>
      { renderStreamingIndicator() }
      <div className={ clsx( styles.layout__runs ) }>
        <NativeTable table={ runsTable } onRowClick={ handleRowClick } />
      </div>
    </>
  );

  return (
    <div className={ clsx( 'page' ) }>
      <ToastContainer />
      {
        isLoading
          ? <Loader />
          : render()
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <EvaluationRunsPage />
    </Suspense>
  );
}
